import React, { useEffect, useRef, useState } from 'react';
import { companyData } from '../mock';

const InteractiveNetworkGlobe = ({ isDarkMode, onServiceClick }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const rotationRef = useRef(0);
  const particlesRef = useRef([]);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [serviceNodes, setServiceNodes] = useState([]);
  const [isVisible, setIsVisible] = useState(true);

  // Observe when canvas is in viewport to pause/resume animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);

    return () => {
      observer.unobserve(canvas);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { 
      alpha: true,
      desynchronized: true // Performance boost
    });
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 480;

    // Reduce complexity on mobile
    const isMobile = window.innerWidth <= 768;
    const pointCount = isMobile ? 50 : 80;
    const particleCount = isMobile ? 10 : 15;

    // Generate points on sphere surface
    const generateSpherePoints = (count) => {
      const points = [];
      const phi = Math.PI * (3 - Math.sqrt(5)); // Golden angle

      for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2;
        const radiusAtY = Math.sqrt(1 - y * y);
        const theta = phi * i;

        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        points.push({ x, y, z });
      }
      return points;
    };

    // Create specific service nodes positioned strategically on the sphere
    const createServiceNodes = () => {
      const services = companyData.services.slice(0, 8); // Use first 8 services
      const nodes = [];
      
      // Position services evenly around the sphere
      services.forEach((service, index) => {
        const angle = (index / services.length) * Math.PI * 2;
        const heightOffset = Math.sin(index * 0.7) * 0.6; // Vary height
        
        nodes.push({
          service: service,
          x: Math.cos(angle) * 0.8,
          y: heightOffset,
          z: Math.sin(angle) * 0.8,
          pulsePhase: index * 0.5 // For pulsing animation
        });
      });
      
      return nodes;
    };

    // Create network connections
    const points = generateSpherePoints(pointCount);
    const connections = [];
    
    // Create connections between nearby points
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const dx = points[i].x - points[j].x;
        const dy = points[i].y - points[j].y;
        const dz = points[i].z - points[j].z;
        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (distance < 0.5) {
          connections.push([i, j]);
        }
      }
    }

    const nodes = createServiceNodes();
    setServiceNodes(nodes);

    // Initialize traveling particles on connections
    const initParticles = () => {
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i++) {
        const connectionIndex = Math.floor(Math.random() * connections.length);
        particlesRef.current.push({
          connectionIndex,
          progress: Math.random(),
          speed: 0.003 + Math.random() * 0.005,
          direction: Math.random() > 0.5 ? 1 : -1
        });
      }
    };

    initParticles();

    // Mouse interaction
    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Check if mouse is over any service node
      let foundNode = null;
      nodes.forEach((node, index) => {
        const rotated = rotatePoint(node, rotationRef.current);
        if (rotated.z > -0.3) { // Only check front-facing nodes
          const scale = 300 / (300 + rotated.z * 100);
          const screenX = centerX + rotated.x * radius * scale;
          const screenY = centerY + rotated.y * radius * scale;
          const distance = Math.sqrt(
            Math.pow(mouseX - screenX, 2) + Math.pow(mouseY - screenY, 2)
          );
          
          if (distance < 35) { // Hit detection radius
            foundNode = index;
          }
        }
      });

      setHoveredNode(foundNode);
      canvas.style.cursor = foundNode !== null ? 'pointer' : 'default';
    };

    const handleClick = (event) => {
      if (hoveredNode !== null && onServiceClick) {
        const node = nodes[hoveredNode];
        onServiceClick(node.service);
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);

    // Rotation helper
    const rotatePoint = (point, rotation) => {
      // Rotate around Y-axis
      const cosY = Math.cos(rotation);
      const sinY = Math.sin(rotation);
      const x = point.x * cosY - point.z * sinY;
      const z = point.x * sinY + point.z * cosY;

      // Rotate around X-axis (slight tilt)
      const cosX = Math.cos(rotation * 0.3);
      const sinX = Math.sin(rotation * 0.3);
      const y = point.y * cosX - z * sinX;
      const z2 = point.y * sinX + z * cosX;

      return { x, y, z: z2 };
    };

    // Animate
    const animate = () => {
      // Pause animation when canvas is not visible (stop BEFORE requesting next frame)
      if (!isVisible) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      rotationRef.current += 0.0015;
      const rotation = rotationRef.current;

      // Rotate points
      const rotatedPoints = points.map(point => rotatePoint(point, rotation));

      // Set colors based on theme
      const primaryColor = isDarkMode ? 'rgba(100, 180, 255, 0.4)' : 'rgba(0, 100, 200, 0.3)';
      const lineWidth = isMobile ? 2 : 1;

      // Draw connections
      ctx.strokeStyle = primaryColor;
      ctx.lineWidth = lineWidth;

      connections.forEach(([i, j]) => {
        const p1 = rotatedPoints[i];
        const p2 = rotatedPoints[j];

        const scale1 = 300 / (300 + p1.z * 100);
        const scale2 = 300 / (300 + p2.z * 100);

        const x1 = centerX + p1.x * radius * scale1;
        const y1 = centerY + p1.y * radius * scale1;
        const x2 = centerX + p2.x * radius * scale2;
        const y2 = centerY + p2.y * radius * scale2;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      });

      // Draw regular points
      rotatedPoints.forEach(point => {
        const scale = 300 / (300 + point.z * 100);
        const x = centerX + point.x * radius * scale;
        const y = centerY + point.y * radius * scale;
        const baseSize = isMobile ? 3 : 2;
        const size = baseSize * scale;

        const brightness = (point.z + 1) / 2;
        const alpha = 0.3 + brightness * 0.5;

        ctx.fillStyle = isDarkMode 
          ? `rgba(100, 200, 255, ${alpha})` 
          : `rgba(0, 120, 220, ${alpha})`;
        
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Update and draw traveling particles
      particlesRef.current.forEach(particle => {
        particle.progress += particle.speed * particle.direction;
        
        if (particle.progress > 1 || particle.progress < 0) {
          particle.progress = particle.progress > 1 ? 0 : 1;
          if (Math.random() > 0.7) {
            particle.direction *= -1;
          }
        }

        const [i, j] = connections[particle.connectionIndex];
        const p1 = rotatedPoints[i];
        const p2 = rotatedPoints[j];

        const progress = particle.progress;
        const px = p1.x + (p2.x - p1.x) * progress;
        const py = p1.y + (p2.y - p1.y) * progress;
        const pz = p1.z + (p2.z - p1.z) * progress;

        if (pz > -0.3) {
          const scale = 300 / (300 + pz * 100);
          const x = centerX + px * radius * scale;
          const y = centerY + py * radius * scale;
          const size = 3 * scale;

          const glowColor = isDarkMode 
            ? 'rgba(0, 200, 255, 0.8)' 
            : 'rgba(0, 120, 220, 0.8)';
          
          // Outer glow
          ctx.beginPath();
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, size * 3);
          gradient.addColorStop(0, isDarkMode ? 'rgba(0, 200, 255, 0.6)' : 'rgba(0, 120, 220, 0.6)');
          gradient.addColorStop(1, 'rgba(0, 200, 255, 0)');
          ctx.fillStyle = gradient;
          ctx.arc(x, y, size * 3, 0, Math.PI * 2);
          ctx.fill();

          // Core particle
          ctx.fillStyle = glowColor;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Draw service nodes (larger, interactive)
      const time = Date.now() * 0.001;
      nodes.forEach((node, index) => {
        const rotated = rotatePoint(node, rotation);
        
        if (rotated.z > -0.3) { // Only draw front-facing nodes
          const scale = 300 / (300 + rotated.z * 100);
          const x = centerX + rotated.x * radius * scale;
          const y = centerY + rotated.y * radius * scale;
          
          // Pulsing effect
          const pulse = 1 + Math.sin(time * 2 + node.pulsePhase) * 0.15;
          const isHovered = index === hoveredNode;
          const baseSize = isHovered ? 28 : 20;
          const size = baseSize * scale * pulse;

          // Glow
          ctx.beginPath();
          const gradient = ctx.createRadialGradient(x, y, 0, x, y, size * 2);
          gradient.addColorStop(0, isHovered 
            ? 'rgba(0, 113, 227, 0.9)' 
            : 'rgba(0, 113, 227, 0.6)');
          gradient.addColorStop(1, 'rgba(0, 113, 227, 0)');
          ctx.fillStyle = gradient;
          ctx.arc(x, y, size * 2, 0, Math.PI * 2);
          ctx.fill();

          // Main node
          ctx.fillStyle = isHovered 
            ? '#00a8ff' 
            : (isDarkMode ? '#0071e3' : '#0056b3');
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fill();

          // Border
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2 * scale;
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.stroke();

          // Label on hover
          if (isHovered) {
            ctx.font = `${14 * scale}px system-ui, -apple-system, sans-serif`;
            ctx.fillStyle = isDarkMode ? '#ffffff' : '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            // Background for text
            const textWidth = ctx.measureText(node.service.title).width;
            ctx.fillStyle = isDarkMode 
              ? 'rgba(0, 0, 0, 0.8)' 
              : 'rgba(255, 255, 255, 0.9)';
            ctx.fillRect(
              x - textWidth / 2 - 8, 
              y + size + 10, 
              textWidth + 16, 
              24
            );
            
            // Text
            ctx.fillStyle = isDarkMode ? '#ffffff' : '#000000';
            ctx.fillText(node.service.title, x, y + size + 22);
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
    };
  }, [isDarkMode, hoveredNode, onServiceClick, isVisible]);

  return (
    <canvas
      ref={canvasRef}
      width={1000}
      height={1000}
      className="network-globe-canvas"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'auto',
        opacity: isDarkMode ? 0.6 : 0.4,
        zIndex: 0
      }}
    />
  );
};

export default InteractiveNetworkGlobe;
